import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
} from "@mui/material";
import { Receipt } from "@mui/icons-material";
import {
  useGetAllClients,
  useGetTssById,
} from "../../../../../stores/Taxation/taxation.queries";
import { useGetClientTransactions } from "../../../../../stores/Taxation/taxation.queries";

const TransactionSettings: React.FC = () => {
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [selectedTssId, setSelectedTssId] = useState<string>("");
  const [limit, setLimit] = useState<number>(10); // Items per page
  const [offset, setOffset] = useState<number>(0); // Current offset
  const [maxKnownPage, setMaxKnownPage] = useState<number>(1); // Track highest page with data

  // Fetch clients for dropdown
  const { data: clientsList = [] } = useGetAllClients();

  // Get the TSS ID for the selected client
  const selectedClient = clientsList.find((c) => c._id === selectedClientId);
  const tssId = selectedClient?.tss_id || selectedTssId;

  // Fetch TSS details - only when we have a valid tssId from selectedClient
  const { data: tssDetails } = useGetTssById(
    tssId && selectedClient ? tssId : "",
  );

  // Fetch transactions (only when both IDs are available)
  const { data, isLoading, error } = useGetClientTransactions(
    tssId || "",
    selectedClientId || "",
    limit,
    offset,
  );

  const hasMore = data?.pagination?.hasMore;
  const transactions = data?.data || [];

  // Calculate current page and total pages
  const currentPage = Math.floor(offset / limit) + 1;

  // Update max known page based on current data
  const currentMaxKnownPage =
    transactions.length > 0
      ? Math.max(maxKnownPage, currentPage)
      : currentPage > 1 && transactions.length === 0
        ? currentPage - 1
        : maxKnownPage;

  // Calculate the maximum page count we know about
  const pageCount =
    transactions.length > 0 && hasMore
      ? Math.max(currentPage + 1, currentMaxKnownPage)
      : currentMaxKnownPage;

  // Handle page change
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    const newOffset = (page - 1) * limit;
    setOffset(newOffset);

    // Update max known page if we're navigating to a page we haven't seen
    if (page > maxKnownPage && transactions.length > 0) {
      setMaxKnownPage(page);
    }
  };

  // Handle client selection change
  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);
    setOffset(0);
    setMaxKnownPage(1);
  };

  const getReceiptTypeChip = (type: string) => {
    const typeConfig: Record<
      string,
      {
        label: string;
        bgcolor: string;
        color: string;
      }
    > = {
      RECEIPT: {
        label: "Beleg",
        bgcolor: "#4caf50",
        color: "#fff",
      },
      TRAINING: {
        label: "Schulung",
        bgcolor: "#ff9800",
        color: "#fff",
      },
      CANCELLATION: {
        label: "Stornierung",
        bgcolor: "#f44336",
        color: "#fff",
      },
      INFO_ORDER: {
        label: "Info Bestellung",
        bgcolor: "#2196f3",
        color: "#fff",
      },
      INFO_INTERNAL: {
        label: "Info Intern",
        bgcolor: "#9c27b0",
        color: "#fff",
      },
      INFO_INVOICE: {
        label: "Info Rechnung",
        bgcolor: "#00bcd4",
        color: "#fff",
      },
    };

    const config = typeConfig[type] || {
      label: type,
      bgcolor: "#9e9e9e",
      color: "#fff",
    };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bgcolor,
          color: config.color,
          fontWeight: 600,
        }}
      />
    );
  };

  const formatAmount = (amountInCents: number) => {
    return `${amountInCents} €`;
  };

  return (
    <Box sx={{ height: "100%", overflow: "auto", pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 1,
            fontWeight: 600,
            background: "#003059",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Transaktionsverwaltung
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verwalten Sie Ihre Client-Transaktionen
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12}>
          {/* Action Bar */}
          <Paper
            sx={{
              p: 2,
              mb: 2,
              background: " #003059",
              color: "white",
              borderRadius: 1.5,
            }}
          >
            <FormControl
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#00509e",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#003059",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(0,0,0,0.6)",
                  "&.Mui-focused": {
                    color: "#003059",
                  },
                },
              }}
            >
              <InputLabel>Client auswählen</InputLabel>
              <Select
                value={selectedClientId}
                label="Client auswählen"
                onChange={(e) => handleClientChange(e.target.value)}
              >
                <MenuItem value="">
                  <em>Bitte wählen</em>
                </MenuItem>
                {clientsList.map((client) => (
                  <MenuItem key={client._id} value={client._id}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, flex: 1 }}
                      >
                        {client._id.substring(0, 8)}...
                      </Typography>
                      <Chip
                        label={client.state}
                        size="small"
                        sx={{
                          bgcolor:
                            client.state === "REGISTERED"
                              ? "#4caf50"
                              : "#ff9800",
                          color: "white",
                          height: 20,
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {/* TSS & Client Info Card */}
          {selectedClient && (
            <Paper
              sx={{ p: 1.5, mb: 2, bgcolor: "#f5f7fa", borderRadius: 1.5 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Client Serial:</strong>{" "}
                    {selectedClient.serial_number}
                  </Typography>
                  <Typography variant="body2">
                    <strong>TSS ID:</strong> {selectedClient.tss_id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {tssDetails && (
                    <>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Business:</strong>{" "}
                        {tssDetails.metadata?.business || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Location:</strong>{" "}
                        {tssDetails.metadata?.location || "N/A"}
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Transaction List */}
          {!selectedClientId ? (
            <Paper sx={{ p: 6, textAlign: "center" }}>
              <Receipt sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Kein Client ausgewählt
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Bitte wählen Sie einen Client aus, um Transaktionen anzuzeigen
              </Typography>
            </Paper>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              Fehler beim Laden der Transaktionen
            </Alert>
          ) : isLoading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Lade Transaktionen...
              </Typography>
            </Box>
          ) : selectedClientId ? (
            <>
              {transactions.length > 0 ? (
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: "500px",
                    mb: 2,
                    boxShadow: 2,
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Nr.
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Transaktionsnummer
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Rezeptnummer
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Datum
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Betrag (€)
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Zahlungsdienst
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "#003059",
                            color: "white",
                          }}
                        >
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((transaction, index) => (
                        <TableRow
                          key={transaction._id}
                          sx={{
                            "&:nth-of-type(odd)": { bgcolor: "#f5f5f5" },
                            "&:hover": { bgcolor: "#e3f2fd" },
                          }}
                        >
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {transaction.transaction_number}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {transaction.metadata?.transactionNumber || "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {transaction.metadata?.rxNumber || "-"}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {transaction.time_creation_formatted}
                          </TableCell>
                          <TableCell
                            sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                          >
                            {formatAmount(
                              transaction.amounts_per_payment_type.reduce(
                                (sum: number, amt: any) => sum + Number(amt.amount),
                                0,
                              ),
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>
                            {transaction.amounts_per_payment_type.length > 0 ? (
                              transaction.amounts_per_payment_type.map(
                                (payment: any, idx: number) => (
                                  <Chip
                                    key={idx}
                                    label={payment.payment_type}
                                    size="small"
                                    sx={{
                                      bgcolor: "#4caf50",
                                      color: "#fff",
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      mb: idx > 0 ? 0.5 : 0,
                                    }}
                                  />
                                ),
                              )
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Keine
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={transaction.state}
                              size="small"
                              sx={{
                                bgcolor:
                                  transaction.state === "ACTIVE"
                                    ? "#4caf50"
                                    : "#ff9800",
                                color: "white",
                                fontSize: "0.7rem",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper sx={{ p: 6, textAlign: "center", mb: 2 }}>
                  <Receipt
                    sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Keine Transaktionen auf dieser Seite
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Bitte navigieren Sie zu einer anderen Seite
                  </Typography>
                </Paper>
              )}

              {/* Pagination Controls - Always show when we have selected a client and have navigated */}
              {currentPage > 1 || transactions.length > 0 ? (
                <Box
                  sx={{
                    mt: 2,
                    mb: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {transactions.length} Transaktionen angezeigt (Seite{" "}
                    {currentPage})
                  </Typography>

                  <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    disabled={isLoading}
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "#89a3ba",
                      },
                      "& .Mui-selected": {
                        bgcolor: "#003059 !important",
                        color: "white",
                      },
                    }}
                  />
                </Box>
              ) : null}
            </>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransactionSettings;
